import React, { useEffect, useState } from 'react';
import './Story.scss';
import adrianImage from '../../assets/img/egg/adrian-story.png';
import useEncounter from 'hooks/useEncounter';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { Check } from 'components/Icons/Icons';
import { completeMission, getMissionsList, UserMissionsListResponse } from 'utils/PointsAPI';
import { Encounter } from 'types/Encounter';
import { useUser } from '../../hooks/useUser';
import partition from 'lodash/partition';
import { routes } from 'routing/routes';
import cls from 'classnames';
import { FLAGS, useFlags } from 'hooks/useFlags';

type EncounterAchievement = Encounter & Partial<UserMissionsListResponse>;

type EncounterLinkProps = {
  encounter: EncounterAchievement;
  isNew?: boolean;
  onClick: (slug: string, link: string, completed?: boolean) => void;
};
const EncounterLink = ({ encounter, isNew, onClick }: EncounterLinkProps) => {
  const { isFlagEnabled } = useFlags();
  const showOmnigrow = !isFlagEnabled(FLAGS.hatchening);

  const encounterDate = new Date(encounter.date);
  return (
    <div key={encounter.id} className={cls('encounter-box', { 'new-encounter': isNew })}>
      <div className="story-title-wrapper">
        <div className="story-date">
          {isNew && (
            <div className="new">
              <span>NEW</span>
            </div>
          )}
          <p>{encounterDate.toLocaleDateString()}</p>
        </div>
        <p className="encounter-title">{encounter.title}</p>
      </div>
      <HorizontalDivider />
      <div className={cls('story-cta', { 'align-right': !showOmnigrow })}>
        {showOmnigrow && (
          <p>
            OmniGrow Reward: <span>{encounter.reward}</span>
          </p>
        )}
        <button
          className={cls('btn btn-primary btn-sm', { 'story-completed': encounter?.completed })}
          onClick={() =>
            onClick(
              encounter.slug,
              routes.interactiveStories.dialog({ slug: encounter.id }),
              encounter?.completed,
            )
          }
        >
          {encounter.completed && <Check />}
          <span>Read</span>
        </button>
      </div>
    </div>
  );
};

export const Story = () => {
  const { encounters: initialEncounters, isLoading } = useEncounter();
  const [encounters, setEncounters] = useState<EncounterAchievement[]>(initialEncounters);
  const { account } = useUser();

  const [newestEncounter, otherEncounters] = partition(
    encounters,
    (encounter) => encounter.order === encounters.at(-1)?.order,
  );

  useEffect(() => {
    if (account.dusktech?.userId && !isLoading && initialEncounters.length > 0) {
      getMissionsList(account.dusktech.userId).then((data) => {
        const encounterAchievements = initialEncounters.map((encounter) => {
          const achievement = data.find((item) => item.slug === encounter.slug);
          return {
            ...encounter,
            completed: achievement?.completed,
            slug: achievement?.slug || '',
            reward: achievement?.reward,
          };
        });
        const sortedEncounters = encounterAchievements.sort((a, b) => a.order - b.order);
        setEncounters(sortedEncounters);
      });
    } else {
      const sortedEncounters = initialEncounters.sort((a, b) => a.order - b.order);
      setEncounters(sortedEncounters);
    }
  }, [initialEncounters, account.dusktech?.userId, isLoading]);

  const markStoryAsRead = async (slug: string, url: string, completed?: boolean) => {
    if (account.dusktech?.userId && !completed) {
      await completeMission(account.dusktech.userId, slug);
    }
    window.location.href = url;
  };
  return (
    <div className="story">
      <div className="story-adrian">
        <img src={adrianImage} alt="Adrian" />
      </div>
      <div className="story-chapters-wrapper">
        <div className="story-chapters-title col-12">
          <p>
            Meet Adrian, a rogue scientist and gribble expert! Not only has he discovered a way to
            incubate gribble eggs on Earth, but he&apos;s also developed the special OmniGrow
            formula to level up your eggs... Join him on his mission to push the boundaries of
            science, and learn more about the world of DuskBreakers along the way.
          </p>
        </div>
        <div className="story-chapters d-flex flex-wrap">
          {!isLoading && newestEncounter.length > 0 && otherEncounters.length > 0 && (
            <>
              <EncounterLink isNew encounter={newestEncounter[0]} onClick={markStoryAsRead} />
              {otherEncounters.map((encounter) => {
                return (
                  <EncounterLink
                    encounter={encounter}
                    onClick={markStoryAsRead}
                    key={encounter.id}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
